/* eslint-disable max-lines -- cohesive single clarification renderer (question + choice/text/picker + resolve), kept in one file alongside its parse/answer wiring */
/**
 * Inline rendering of an ``approval_kind === 'clarification'``
 * (the BE ``ask_user`` tool) right where the originating
 * ``tool_call`` block sits in the transcript.
 *
 * Visual contract
 * ---------------
 * The card is a plain surface card (no info-blue tint, no helper
 * icon tile, no "Heidi has a question" subtitle). The question is
 * the prominent header, with a small ``"X of N"`` pagination
 * indicator + a close (``deny``) button on the right. Options are
 * rendered as full-width clickable rows with a lettered badge
 * (A, B, C, …) on the left. The footer is ``Skip`` (deny) + a
 * primary ``Continue`` (approve) button that is disabled until
 * the user has produced a valid answer per ``buildAnswer``.
 *
 * Wire path differs from ``ApprovalRequestBlock``
 * -----------------------------------------------
 * ``ApprovalRequestBlock`` posts to
 * ``/projects/{id}/approvals/{id}:resolve`` (the projects router; the
 * floating overlay AND the projects chat both go through it). This
 * block goes through the **harness** ``resolveToolApproval`` API
 * (``/agents/runs/{run}/approvals/{id}:resolve``) instead, because
 * ``ask_user`` is the only clarification surface the rest of the
 * harness pipeline (atom, ``ToolApprovalHost``, modal fallback)
 * already wires through that path — routing the inline card through
 * the projects router would split the atom cleanup across two
 * branches for no real gain.
 *
 * On resolve we invalidate the inbox React Query cache so the card
 * unmounts once the refetch confirms the approval is no longer pending.
 *
 * Why this duplicates pieces of ``AskUserDetails``
 * ------------------------------------------------
 * ``ask-user-details.tsx`` / ``ask-user-details.choices.tsx`` still
 * power the modal-fallback render (radio / checkbox rows). The
 * inline lettered-badge UI is a Projects-only redesign and lives
 * here so we can iterate on the visual contract without disturbing
 * the modal surface. Both surfaces share the same wire shape via
 * ``parseInput`` / ``buildAnswer`` from ``ask-user-details.types``.
 */

import { Button } from '@heidi/design-system/button';
import { Form, FormControl, FormField, FormItem } from '@heidi/design-system/form';
import { SearchInput } from '@heidi/design-system/search-input';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { logger } from '@heidi/runtime/logger';
import { cn } from '@heidi/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { Loader2 } from 'lucide-react';
import type { ComponentType, JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import type {
  AskUserOption,
  ParsedClarificationQuestion,
  ResolvedAnswer,
} from '../../../contracts/ask-user-details.types';
import type { LinkedContextItem } from '../../../contracts/projects/linked-context';
import type { PendingToolApproval } from '../../../contracts/tool-approval.types';
// The data barrel, not the declaring modules: it is the module the
// `@heidi/agents/data` door resolves to, so a suite's door mock still reaches it.
import {
  buildAnswer,
  buildClarificationModifiedInput,
  clarificationAnswerDraftAtom,
  clarificationAnswersComplete,
  composerLinkedItemsAtom,
  isWaitingForFoldedTextClarification,
  LEGACY_SINGLE_QUESTION_ID,
  letterFor,
  OTHER_OPTION_ID,
  OTHER_WIRE_VALUE,
  parseQuestions,
  partitionClarificationQuestions,
  usePaginationLabel,
  usePendingApprovalsForRun,
} from '../../../data';
import { useResolveApproval } from '../../tool-approvals/useResolveApproval';
import {
  ClarificationLoginComputer as BoundClarificationLoginComputer,
  type ClarificationLoginComputerProps,
} from './ClarificationLoginComputer';
import { ConnectAppChips } from './ConnectAppChips';
import {
  EhrPickerList as BoundEhrPickerList,
  EHR_OTHER_PROVIDER_ID,
  type EhrPickerListProps,
} from './EhrPickerList';
import { OptionRow, OtherRow } from './InlineClarificationBlock.parts';
import {
  PatientPickerList as BoundPatientPickerList,
  type PatientPickerListProps,
} from './PatientPickerList';
import {
  SessionDocumentPickerList as BoundSessionDocumentPickerList,
  type SessionDocumentPickerListProps,
} from './SessionDocumentPickerList';
import {
  SessionPickerList as BoundSessionPickerList,
  type SessionPickerListProps,
} from './SessionPickerList';
import {
  TemplatePickerList as BoundTemplatePickerList,
  type TemplatePickerListProps,
} from './TemplatePickerList';

export interface InlineClarificationBlockProps {
  approval: PendingToolApproval;
  /** Child runs have no composer of their own, so their card collects text too. */
  textAnswerLocation?: 'composer' | 'card';
  /** Fit the question heading to the narrow computer-use status overlay. */
  compact?: boolean;
}

/** What a ``connect_app`` step hands the app's connect chips. */
export interface ClarificationConnectAppArgs {
  connectors: readonly AskUserOption[];
  connectedIds: ReadonlySet<string>;
  onConnected: (connector: string) => void;
  disabled: boolean;
}

/**
 * The app-bound answer controls: the self-fetching pickers and the login live
 * view as components, the connected-apps chips as a slot. Pass one stable object.
 */
export interface InlineClarificationBlockHost {
  SessionPickerList: ComponentType<SessionPickerListProps>;
  PatientPickerList: ComponentType<PatientPickerListProps>;
  TemplatePickerList: ComponentType<TemplatePickerListProps>;
  EhrPickerList: ComponentType<EhrPickerListProps>;
  SessionDocumentPickerList: ComponentType<SessionDocumentPickerListProps>;
  ClarificationLoginComputer: ComponentType<ClarificationLoginComputerProps>;
  renderConnectApp: (args: ClarificationConnectAppArgs) => ReactNode;
}

/** In-progress answer state for one step, stashed so Back/Next can
 *  restore what the user already entered on a previously-visited step. */
interface StepWorking {
  selectedIds: string[];
  textValue: string;
  otherText: string;
}

const EMPTY_WORKING: StepWorking = {
  selectedIds: [],
  textValue: '',
  otherText: '',
};

const textAnswerSchema = z.object({ answer: z.string().trim().min(1) });
const MAX_VISIBLE_OPTIONS = 5;

/**
 * Build one step's resolved answer from its working state. Wraps
 * ``buildAnswer`` with the ``pick_ehr`` "Other" rule (the typed provider
 * name must ride in ``text`` alongside the Other id), so the same logic
 * is shared between the live step's Continue-gate and the final submit
 * that serialises every step.
 */
const buildStepAnswer = (
  question: ParsedClarificationQuestion,
  working: StepWorking
): ResolvedAnswer | null => {
  if (
    question.kind === 'pick_ehr' &&
    working.selectedIds.includes(EHR_OTHER_PROVIDER_ID)
  ) {
    const detail = working.otherText.trim();
    if (!detail) return null;
    const base: ResolvedAnswer = {
      selected_option_ids: [...working.selectedIds],
      text: detail,
    };
    return base;
  }
  const answer = buildAnswer(
    question,
    new Set(working.selectedIds),
    working.textValue,
    working.otherText
  );
  if (!answer) return null;
  return answer;
};

export const InlineClarificationBlockView = ({
  approval,
  textAnswerLocation = 'composer',
  compact = false,
  host: {
    SessionPickerList,
    PatientPickerList,
    TemplatePickerList,
    EhrPickerList,
    SessionDocumentPickerList,
    ClarificationLoginComputer,
    renderConnectApp,
  },
}: InlineClarificationBlockProps & {
  host: InlineClarificationBlockHost;
}): JSX.Element | null => {
  const intl = useIntl();
  const { resolve, pendingVerdict, isLocked, reset } = useResolveApproval(approval, {
    logTag: '[inline-clarification]',
  });
  const parsedQuestions = useMemo(
    () => parseQuestions(approval.toolInput),
    [approval.toolInput]
  );
  const questions = useMemo(
    () =>
      parsedQuestions
        ? textAnswerLocation === 'card'
          ? parsedQuestions
          : partitionClarificationQuestions(parsedQuestions).structured
        : null,
    [parsedQuestions, textAnswerLocation]
  );
  const [drafts, setDrafts] = useAtom(clarificationAnswerDraftAtom);
  const jotaiStore = useStore();
  const draft = drafts[approval.approvalId] ?? {};

  const allPending = usePendingApprovalsForRun(approval.agentRunId);
  const { paginationLabel } = usePaginationLabel(allPending, approval.approvalId);

  // The composer-linked session is the fallback target for the
  // ``pick_document`` picker when the agent didn't stamp a ``session_id``
  // into the clarification (the run's own session).
  const composerLinkedItems = useAtomValue(composerLinkedItemsAtom);

  const [stepIndex, setStepIndex] = useState(0);
  // In-progress answer state for the OTHER (non-active) steps, so Back /
  // Next restores what the user already entered. The active step's state
  // lives in the live ``selectedIds`` / ``textValue`` / ``otherText``.
  // Held in a ref (not state) because every mutation is paired with a
  // ``setStepIndex`` / ``setSkippedIds`` that already re-renders, and the
  // step-hydration effect + final submit must read it synchronously.
  const workingByStepRef = useRef<Record<string, StepWorking>>({});
  const setWorking = useCallback(
    (updater: (prev: Record<string, StepWorking>) => Record<string, StepWorking>) => {
      workingByStepRef.current = updater(workingByStepRef.current);
    },
    []
  );
  // Questions the user explicitly skipped — they serialise to ``null`` in
  // the batched answer map (distinct from dismissing the whole card).
  const [skippedIds, setSkippedIds] = useState<Set<string>>(() => new Set());

  // Active-step answer state. Reset on approval swap (and re-hydrated on
  // step change) so a stale selection can't leak across questions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const textAnswerForm = useForm<z.infer<typeof textAnswerSchema>>({
    resolver: zodResolver(textAnswerSchema),
    mode: 'onChange',
    defaultValues: { answer: '' },
  });
  const textValue = textAnswerForm.watch('answer');
  const { setValue } = textAnswerForm;
  const setTextValue = useCallback(
    (value: string) => setValue('answer', value, { shouldValidate: true }),
    [setValue]
  );
  const [otherText, setOtherText] = useState('');
  const [optionQuery, setOptionQuery] = useState('');
  const [isAtOptionListEnd, setIsAtOptionListEnd] = useState(false);
  const otherInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setStepIndex(0);
    workingByStepRef.current = {};
    setSkippedIds(new Set());
    setSelectedIds(new Set());
    setTextValue('');
    setOtherText('');
    setOptionQuery('');
    // Clear latched verdict so a parent that swaps the approval without
    // unmounting us doesn't leave the next card permanently locked.
    reset();
  }, [approval.approvalId, reset, setTextValue]);

  const current = questions ? (questions[stepIndex] ?? questions[0]) : null;
  const currentId = current?.id;

  useEffect(() => {
    setIsAtOptionListEnd(false);
  }, [optionQuery, currentId, approval.approvalId]);

  // Re-hydrate the active-step state from saved working state whenever the
  // step changes (Back / Next). Reads the ref so it always sees the value
  // persisted by the navigation handler in the same tick.
  useEffect(() => {
    if (!currentId) return;
    const saved = workingByStepRef.current[currentId];
    setSelectedIds(new Set(saved?.selectedIds ?? []));
    setTextValue(saved?.textValue ?? '');
    setOtherText(saved?.otherText ?? '');
    setOptionQuery('');
  }, [stepIndex, currentId, setTextValue]);

  // Mirror the active step's in-progress answer through ``buildStepAnswer``
  // so the Next / Continue enable state matches the eventual resolve
  // payload exactly.
  const modifiedInput = useMemo(() => {
    if (!current) return null;
    return buildStepAnswer(current, {
      selectedIds: Array.from(selectedIds),
      textValue,
      otherText,
    });
  }, [current, selectedIds, textValue, otherText]);

  // Unparseable tool_input must not crash the chat — render a free-text
  // fallback so the pending approval stays resolvable. All-text batches
  // and mixed batches still waiting on a folded text reply stay in the
  // parent transcript. Child-run cards collect every question themselves.
  if (
    textAnswerLocation === 'composer' &&
    (isWaitingForFoldedTextClarification(approval.toolInput, draft) ||
      (parsedQuestions && questions?.length === 0))
  ) {
    return null;
  }
  if (!questions || !current) {
    return <ClarificationFallback approval={approval} compact={compact} />;
  }

  const totalSteps = questions.length;
  const isBatch = totalSteps > 1;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex >= totalSteps - 1;
  const isCurrentSkipped = currentId ? skippedIds.has(currentId) : false;

  const isMulti = current.maxSelections > 1 && current.kind !== 'text';
  // Agent-supplied option picker (it enumerated specific sessions/
  // patients/files): render its lettered option rows. A ``pick_session`` /
  // ``pick_patient`` with NO options is the self-fetching picker instead;
  // an options-less ``pick_file`` already degraded to ``text`` at parse.
  const isOptionPicker =
    (current.kind === 'pick_session' ||
      current.kind === 'pick_patient' ||
      current.kind === 'pick_file') &&
    current.options.length > 0;
  const isSessionPicker = current.kind === 'pick_session' && current.options.length === 0;
  const isPatientPicker = current.kind === 'pick_patient' && current.options.length === 0;
  const isTemplatePicker = current.kind === 'pick_template';
  const isDocumentPicker = current.kind === 'pick_document';
  const isEhrPicker = current.kind === 'pick_ehr';
  const isConnectApp = current.kind === 'connect_app';
  // ``login_computer`` — the computer-use run hit an app sign-in page.
  const isLoginComputer = current.kind === 'login_computer';
  // ``pick_document`` targets the agent's session (carried on the
  // clarification) or, failing that, whatever session is linked in the
  // composer. ``selectedId`` is the single armed pick (single-select).
  const linkedSessionId = composerLinkedItems.find(
    (item): item is Extract<LinkedContextItem, { type: 'session' }> =>
      item.type === 'session'
  )?.sessionId;
  const documentSessionId = current.sessionId ?? linkedSessionId;
  const armedId = Array.from(selectedIds)[0];
  const allowOther = current.allowOther && current.kind === 'choice';
  const otherIsSelected = allowOther && selectedIds.has(OTHER_OPTION_ID);

  // A step is "ready" when it has a submittable answer OR was skipped.
  // The active step reads the live ``modifiedInput``; other steps read
  // their stashed working state.
  const isStepReady = (question: ParsedClarificationQuestion): boolean => {
    if (skippedIds.has(question.id)) return true;
    if (question.id === currentId) {
      return (
        modifiedInput !== null &&
        (question.kind !== 'text' || textAnswerForm.formState.isValid)
      );
    }
    const working = workingByStepRef.current[question.id] ?? EMPTY_WORKING;
    return buildStepAnswer(question, working) !== null;
  };
  const currentStepReady = isStepReady(current);
  const allStepsReady = questions.every(isStepReady);
  // ``Next`` only needs THIS step resolved; ``Continue`` (final submit)
  // needs every step resolved.
  const nextDisabled = isLocked || !currentStepReady;
  const continueDisabled = isLocked || !allStepsReady;

  const persistActive = () => {
    if (!currentId) return;
    setWorking((prev) => ({
      ...prev,
      [currentId]: {
        selectedIds: Array.from(selectedIds),
        textValue,
        otherText,
      },
    }));
  };
  const goToStep = (next: number) => {
    persistActive();
    setStepIndex(Math.max(0, Math.min(next, totalSteps - 1)));
  };
  const skipCurrent = () => {
    if (!currentId) return;
    setSkippedIds((prev) => new Set(prev).add(currentId));
    setWorking((prev) => ({ ...prev, [currentId]: { ...EMPTY_WORKING } }));
    // Clear the live state now — step-hydration only runs on step change,
    // so on the last step (no advance) the active controls must reset here.
    setSelectedIds(new Set());
    setTextValue('');
    setOtherText('');
    setOptionQuery('');
    if (!isLastStep) setStepIndex(stepIndex + 1);
  };
  // The primary button advances the stepper: Next mid-batch, the final
  // approve submit on the last step. Shared by the button + Enter / Other
  // keyboard shortcuts so they never diverge.
  const advance = () => {
    if (isLastStep) {
      if (!continueDisabled) void submit('approve');
    } else if (!nextDisabled) {
      goToStep(stepIndex + 1);
    }
  };

  // Editing a step's controls un-skips it (the user changed their mind).
  const unskipCurrent = () => {
    if (currentId && skippedIds.has(currentId)) {
      setSkippedIds((prev) => {
        const next = new Set(prev);
        next.delete(currentId);
        return next;
      });
    }
  };

  function collectCardAnswers(): Record<string, ResolvedAnswer | null> {
    const answers: Record<string, ResolvedAnswer | null> = {};
    for (const question of questions ?? []) {
      if (skippedIds.has(question.id)) {
        answers[question.id] = null;
        continue;
      }
      const working =
        question.id === currentId
          ? { selectedIds: Array.from(selectedIds), textValue, otherText }
          : (workingByStepRef.current[question.id] ?? EMPTY_WORKING);
      answers[question.id] = buildStepAnswer(question, working);
    }
    return answers;
  }

  const dropDraft = () => {
    setDrafts((prev) => {
      if (!(approval.approvalId in prev)) return prev;
      const next = { ...prev };
      delete next[approval.approvalId];
      return next;
    });
  };

  function submit(verdict: 'approve' | 'deny'): Promise<void> {
    if (verdict === 'deny') {
      dropDraft();
      return resolve(verdict, () => undefined);
    }
    const latestDraft =
      jotaiStore.get(clarificationAnswerDraftAtom)[approval.approvalId] ?? {};
    const merged = { ...latestDraft, ...collectCardAnswers() };
    setDrafts((prev) => ({ ...prev, [approval.approvalId]: merged }));
    if (!parsedQuestions || !clarificationAnswersComplete(parsedQuestions, merged)) {
      return Promise.resolve();
    }
    return resolve(verdict, () =>
      buildClarificationModifiedInput(parsedQuestions, merged)
    ).then(() => {
      dropDraft();
    });
  }

  const toggleOption = (id: string) => {
    unskipCurrent();
    setSelectedIds((prev) => {
      // Picker / single-choice: replacement semantics. Selecting
      // any named option also clears Other (Other is exclusive
      // with named options across both modes — see header).
      if (!isMulti) {
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      next.delete(OTHER_OPTION_ID);
      // Cap honours the BE-supplied limit. Option lists disable
      // unselected rows at cap; this keep-unchanged branch is the
      // backstop for the picker lists, which have no such affordance.
      if (next.size >= current.maxSelections) return prev;
      next.add(id);
      return next;
    });
  };

  const selectOther = () => {
    unskipCurrent();
    setSelectedIds(new Set([OTHER_OPTION_ID]));
    // Defer focus by a frame so the input has actually mounted /
    // become interactive before we ask for focus.
    setTimeout(() => otherInputRef.current?.focus(), 0);
  };

  const handleOtherTextChange = (value: string) => {
    setOtherText(value);
    // Auto-promote Other to selected when the user starts typing —
    // matches the spec ("when the input has text, it counts as
    // selected"). Other is exclusive with named options.
    if (value.trim().length > 0) {
      unskipCurrent();
      setSelectedIds(new Set([OTHER_OPTION_ID]));
    }
  };

  // Single-select pickers (template / EHR / document) set ``selectedIds``
  // directly. Route them through here so a selection on a previously-
  // skipped step clears the skip flag too (matching ``toggleOption``).
  // Without this the visible pick is silently serialised as ``null`` at
  // submit because the step is still in ``skippedIds``.
  const selectSingle = (id: string) => {
    unskipCurrent();
    setSelectedIds(new Set([id]));
  };
  const handleEhrOtherValueChange = (value: string) => {
    if (value.trim().length > 0) unskipCurrent();
    setOtherText(value);
  };

  const renderOptionList = (options: AskUserOption[]) => {
    // When the LLM emits ``allow_other: true`` it sometimes ALSO
    // includes an "Other" entry in ``options`` — producing two rows
    // that select the same logical answer (one as a plain choice
    // row, one as the FE-appended free-text input). Drop any
    // LLM-supplied option whose id or label reads as "other" so the
    // appended ``OtherRow`` is the single source of truth for that
    // affordance. Without this dedupe the card renders e.g.
    // ``D Other`` + ``E Type your answer…`` for the same choice.
    const displayedOptions = allowOther
      ? options.filter(
          (o) =>
            o.id.trim().toLowerCase() !== OTHER_WIRE_VALUE &&
            o.label.trim().toLowerCase() !== 'other'
        )
      : options;
    const isLong = displayedOptions.length > MAX_VISIBLE_OPTIONS;
    const normalizedQuery = optionQuery.trim().toLowerCase();
    const indexedOptions = displayedOptions.map((opt, idx) => ({
      opt,
      badge: letterFor(idx),
    }));
    const filteredOptions =
      isLong && normalizedQuery
        ? indexedOptions.filter(
            ({ opt }) =>
              opt.label.toLowerCase().includes(normalizedQuery) ||
              opt.description?.toLowerCase().includes(normalizedQuery)
          )
        : indexedOptions;
    const showBottomFade = isLong && filteredOptions.length > MAX_VISIBLE_OPTIONS;
    // At the cap an unselected row is unselectable rather than silently
    // inert — over-cap must be unrepresentable (the BE drops an over-cap
    // submission wholesale as a skip). Selected rows stay interactive so
    // the user can deselect.
    const atCap = isMulti && selectedIds.size >= current.maxSelections;
    return (
      <div className="flex flex-col gap-1">
        {isLong && (
          <SearchInput
            value={optionQuery}
            onChange={(event) => setOptionQuery(event.target.value)}
            disabled={isLocked}
            placeholder={intl.formatMessage({
              id: 'lYgYrVnNCf',
              defaultMessage: 'Search options',
              description: 'Placeholder for filtering a long clarification option list',
            })}
            aria-label={intl.formatMessage({
              id: 'HSq4NeP9Ha',
              defaultMessage: 'Search options',
              description:
                'Accessible label for filtering a long clarification option list',
            })}
            clearAriaLabel={intl.formatMessage({
              id: 'j+pYJlykVA',
              defaultMessage: 'Clear search',
              description: 'Accessible label for clearing an option search',
            })}
          />
        )}
        <div
          className={cn(
            isLong && 'scrollbar-fade max-h-60 overflow-y-auto',
            'flex flex-col gap-1',
            showBottomFade &&
              !isAtOptionListEnd &&
              '[mask-image:linear-gradient(to_bottom,black_calc(100%-2.5rem),transparent)]'
          )}
          onScroll={(event) => {
            const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
            setIsAtOptionListEnd(scrollTop + clientHeight >= scrollHeight - 1);
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map(({ opt, badge }) => (
              <OptionRow
                key={opt.id}
                badge={badge}
                label={opt.label}
                description={opt.description}
                isSelected={selectedIds.has(opt.id)}
                onClick={() => toggleOption(opt.id)}
                disabled={isLocked || (atCap && !selectedIds.has(opt.id))}
              />
            ))
          ) : isLong ? (
            <TypographyCaption className="px-3 py-4 text-text-secondary">
              {intl.formatMessage({
                id: '4MfZNVFuEK',
                defaultMessage: 'No options match your search.',
                description:
                  'Empty state shown when no clarification options match the search',
              })}
            </TypographyCaption>
          ) : null}
        </div>
        {allowOther && (
          <OtherRow
            inputRef={otherInputRef}
            value={otherText}
            onChange={handleOtherTextChange}
            // Clicking the input itself must also flip the row to
            // "selected". Don't reset the text — only swap the
            // selection set.
            onFocus={() => setSelectedIds(new Set([OTHER_OPTION_ID]))}
            onRowClick={selectOther}
            isSelected={otherIsSelected}
            onSubmit={advance}
            placeholder={intl.formatMessage({
              id: 'qm/eL5Y8Fl',
              defaultMessage: 'Something else',
            })}
            disabled={isLocked}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-clarification-request={approval.approvalId}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {isBatch && (
              <span
                className="text-xs font-medium tabular-nums text-text-secondary"
                data-testid="clarification-step-indicator"
              >
                {intl.formatMessage(
                  {
                    id: 'ibUSAQhOt1',
                    defaultMessage: 'Question {current} of {total}',
                  },
                  { current: stepIndex + 1, total: totalSteps }
                )}
              </span>
            )}
            {compact ? (
              <TypographyP2Bold className="break-words text-text-primary">
                {current.question}
              </TypographyP2Bold>
            ) : (
              <TypographyPBold className="break-words text-text-primary">
                {current.question}
              </TypographyPBold>
            )}
          </div>
          {paginationLabel && (
            <span
              className="shrink-0 text-xs font-medium tabular-nums text-text-secondary"
              data-testid="clarification-pagination"
            >
              {paginationLabel}
            </span>
          )}
        </div>

        {isMulti && !isConnectApp && (
          <p className="text-xs text-text-secondary">
            {intl.formatMessage(
              {
                id: '2Ub42Dmn0L',
                defaultMessage:
                  'Pick up to {max, plural, one {# option} other {# options}}.',
              },
              { max: current.maxSelections }
            )}
          </p>
        )}
      </div>

      {current.kind === 'choice' && renderOptionList(current.options)}
      {current.kind === 'text' && (
        <Form {...textAnswerForm}>
          <FormField
            control={textAnswerForm.control}
            name="answer"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    aria-label={current.question}
                    onChange={(event) => {
                      unskipCurrent();
                      field.onChange(event);
                    }}
                    disabled={isLocked}
                    placeholder={intl.formatMessage({
                      id: 'zJHMNwGSKk',
                      defaultMessage: 'Type your answer…',
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Form>
      )}
      {isOptionPicker && renderOptionList(current.options)}

      {isSessionPicker && (
        <SessionPickerList
          selectedIds={selectedIds}
          onToggle={(session) => toggleOption(session.id)}
          disabled={isLocked}
        />
      )}

      {isPatientPicker && (
        <PatientPickerList
          selectedIds={selectedIds}
          onToggle={(patient) => toggleOption(patient.id)}
          disabled={isLocked}
        />
      )}

      {isTemplatePicker && (
        <TemplatePickerList
          selectedId={armedId}
          onSelect={(template) => selectSingle(template.id)}
          disabled={isLocked}
        />
      )}

      {isEhrPicker && (
        <EhrPickerList
          selectedId={armedId}
          onSelect={(ehr) => selectSingle(ehr.id)}
          otherValue={otherText}
          onOtherValueChange={handleEhrOtherValueChange}
          disabled={isLocked}
        />
      )}

      {isConnectApp &&
        renderConnectApp({
          connectors: current.options,
          connectedIds: selectedIds,
          onConnected: (connector) => {
            unskipCurrent();
            const nextIds = new Set(selectedIds).add(connector);
            setSelectedIds(nextIds);
            // Chip-only UX (Merge-style): auto-resume — no Continue click.
            if (isLastStep) {
              void resolve('approve', () => {
                const answers: Record<string, ResolvedAnswer | null> = {};
                for (const question of questions ?? []) {
                  if (skippedIds.has(question.id)) {
                    answers[question.id] = null;
                    continue;
                  }
                  if (question.id === currentId) {
                    answers[question.id] = buildStepAnswer(question, {
                      selectedIds: Array.from(nextIds),
                      textValue: '',
                      otherText: '',
                    });
                    continue;
                  }
                  const working = workingByStepRef.current[question.id] ?? EMPTY_WORKING;
                  answers[question.id] = buildStepAnswer(question, working);
                }
                return buildClarificationModifiedInput(questions ?? [], answers);
              });
              return;
            }
            persistActive();
            setStepIndex(stepIndex + 1);
          },
          disabled: isLocked,
        })}

      {isDocumentPicker &&
        (documentSessionId ? (
          <SessionDocumentPickerList
            sessionId={documentSessionId}
            selectedId={armedId}
            onSelect={(document) => selectSingle(document.id)}
            disabled={isLocked}
          />
        ) : (
          <TypographyCaption className="text-text-secondary">
            {intl.formatMessage({
              id: 'bahuf3XPIU',
              defaultMessage:
                'No session is linked, so there are no documents to choose from.',
            })}
          </TypographyCaption>
        ))}

      {isLoginComputer && (
        <ClarificationLoginComputer
          disabled={isLocked}
          isCloudComputer={current.isCloudComputer}
        />
      )}

      {/* connect_app: chips auto-resume — Skip only, no Continue. */}
      <div className="-mt-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isBatch && !isFirstStep && !isConnectApp && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToStep(stepIndex - 1)}
              disabled={isLocked}
              data-testid="clarification-back"
            >
              {intl.formatMessage({ id: 'cyR7KhiuaU', defaultMessage: 'Back' })}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void submit('deny')}
            disabled={isLocked}
            data-testid="clarification-dismiss"
          >
            {pendingVerdict === 'deny' ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : null}
            {isBatch
              ? intl.formatMessage({ id: 'kIHftMOh4h', defaultMessage: 'Skip all' })
              : intl.formatMessage({ id: '/4tOwTiCH6', defaultMessage: 'Skip' })}
          </Button>
          {isBatch && !isConnectApp && (
            <Button
              variant="outline"
              size="sm"
              onClick={skipCurrent}
              disabled={isLocked || isCurrentSkipped}
              data-testid="clarification-skip-step"
            >
              {intl.formatMessage({
                id: '3UzRvTvXEB',
                defaultMessage: 'Skip question',
              })}
            </Button>
          )}
          {!isConnectApp && (
            <Button
              size="sm"
              onClick={advance}
              disabled={isLastStep ? continueDisabled : nextDisabled}
              data-testid="clarification-primary"
            >
              {pendingVerdict === 'approve' ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : null}
              {!isLastStep
                ? intl.formatMessage({ id: '9+DdtuCqLw', defaultMessage: 'Next' })
                : isLoginComputer
                  ? intl.formatMessage({ id: 'dOafYeXUol', defaultMessage: "I'm done" })
                  : intl.formatMessage({ id: 'acrOozm08x', defaultMessage: 'Continue' })}
            </Button>
          )}
          {isConnectApp && pendingVerdict === 'approve' && (
            <Loader2
              className="size-3.5 animate-spin text-text-secondary"
              data-testid="connect-app-resuming"
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Fallback for a clarification whose ``tool_input`` couldn't be parsed
 * into a structured question (unknown ``kind``, missing options, a
 * schema the FE doesn't recognise). Rather than throw — which blanked
 * the whole transcript via the error boundary and left the run stuck —
 * we render the salvageable question (or a generic prompt) with a
 * free-text answer + Skip, so the approval is always resolvable. Routes
 * through the same harness ``resolveToolApproval`` API as the main card.
 */
const ClarificationFallback = ({
  approval,
  compact = false,
}: InlineClarificationBlockProps): JSX.Element => {
  const intl = useIntl();
  const { resolve, pendingVerdict, isLocked, reset } = useResolveApproval(approval, {
    logTag: '[inline-clarification] fallback',
  });
  const [text, setText] = useState('');

  useEffect(() => {
    // Reset answer state on approval swap — ``InlineClarificationBlock``
    // is rendered without a ``key`` in the composer dock, so an in-place
    // ``approval`` change reuses this instance; without the reset a
    // stale ``text`` / latched ``pendingVerdict`` (isLocked) would carry
    // into the next question. Mirrors the main card's reset effect.
    setText('');
    reset();
    logger.warn('[inline-clarification] unparseable tool_input — rendering fallback', {
      approval_id: approval.approvalId,
      agent_run_id: approval.agentRunId,
    });
  }, [approval.approvalId, approval.agentRunId, reset]);

  const rawQuestion =
    typeof approval.toolInput?.question === 'string'
      ? approval.toolInput.question.trim()
      : '';
  const question =
    rawQuestion ||
    intl.formatMessage({
      id: 'DsGamAlZEK',
      defaultMessage: 'Heidi needs a bit more information to continue.',
    });

  const submit = useCallback(
    async (verdict: 'approve' | 'deny') => {
      if (isLocked) return;
      const trimmed = text.trim();
      if (verdict === 'approve' && !trimmed) return;
      await resolve(verdict, (resolvedVerdict) =>
        resolvedVerdict === 'approve'
          ? {
              answers: {
                [LEGACY_SINGLE_QUESTION_ID]: {
                  selected_option_ids: [],
                  text: trimmed,
                },
              },
            }
          : undefined
      );
    },
    [isLocked, text, resolve]
  );

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-clarification-request={approval.approvalId}
      data-clarification-fallback=""
    >
      {compact ? (
        <TypographyP2Bold className="break-words text-text-primary">
          {question}
        </TypographyP2Bold>
      ) : (
        <TypographyPBold className="break-words text-text-primary">
          {question}
        </TypographyPBold>
      )}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && text.trim() && !isLocked) {
            e.preventDefault();
            void submit('approve');
          }
        }}
        autoFocus
        disabled={isLocked}
        placeholder={intl.formatMessage({
          id: 'zJHMNwGSKk',
          defaultMessage: 'Type your answer…',
        })}
        className={cn(
          'w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-text-primary',
          'placeholder:text-text-secondary',
          'focus:border-border-pressed focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60'
        )}
        data-testid="clarification-fallback-input"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void submit('deny')}
          disabled={isLocked}
        >
          {pendingVerdict === 'deny' ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : null}
          {intl.formatMessage({ id: '/4tOwTiCH6', defaultMessage: 'Skip' })}
        </Button>
        <Button
          size="sm"
          onClick={() => void submit('approve')}
          disabled={isLocked || !text.trim()}
        >
          {pendingVerdict === 'approve' ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : null}
          {intl.formatMessage({ id: 'acrOozm08x', defaultMessage: 'Continue' })}
        </Button>
      </div>
    </div>
  );
};

export const CLARIFICATION_HOST: InlineClarificationBlockHost = {
  SessionPickerList: BoundSessionPickerList,
  PatientPickerList: BoundPatientPickerList,
  TemplatePickerList: BoundTemplatePickerList,
  EhrPickerList: BoundEhrPickerList,
  SessionDocumentPickerList: BoundSessionDocumentPickerList,
  ClarificationLoginComputer: BoundClarificationLoginComputer,
  renderConnectApp: ({ connectors, connectedIds, onConnected, disabled }) => (
    <ConnectAppChips
      connectors={connectors}
      connectedIds={connectedIds}
      onConnected={onConnected}
      disabled={disabled}
      message=""
    />
  ),
};

/** Binds the clarification card to the pickers, the login view and the connect chips. */
export const InlineClarificationBlock = (
  props: InlineClarificationBlockProps
): JSX.Element | null => (
  <InlineClarificationBlockView {...props} host={CLARIFICATION_HOST} />
);
